import React, { useState, useEffect } from "react";
import {
  Modal,
  Input,
  Button,
  Space,
  Typography,
  message as antMessage,
} from "antd";
import { useSelector, useDispatch } from "react-redux";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import api from "../../core/axios/axiosInstance";
import { fetchProfile } from "../../core/data/redux/slices/ProfileSlice";

const { Text } = Typography;

const ProfileCompletionModal = () => {
  const profile = useSelector((state) => state.profile);
  const dispatch = useDispatch();

  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [welcomeFormData, setWelcomeFormData] = useState({
    firstname: "",
    lastname: "",
    phonenumber: "",
  });

  // Check if profile is incomplete (any of the three fields missing)
  // Only show for companyAdmin role
  useEffect(() => {
    const hasFirstname = profile.firstname && profile.firstname.trim() !== "";
    const hasLastname = profile.lastname && profile.lastname.trim() !== "";
    const hasPhone = profile.phonenumbers && profile.phonenumbers.length > 0;
    const isCompanyAdmin = profile.role === "companyAdmin";

    // Show modal if ANY of the three are missing AND user is companyAdmin
    if ((!hasFirstname || !hasPhone) && !profile.isLoading && isCompanyAdmin) {
      setShowWelcomeModal(true);

      // Pre-fill existing values
      let phoneValue = "";
      if (hasPhone) {
        const phoneData = profile.phonenumbers[0];
        // Check if it's an object with countryCode and number, or just a string
        if (
          typeof phoneData === "object" &&
          phoneData.countryCode &&
          phoneData.number
        ) {
          phoneValue = `${phoneData.countryCode}${phoneData.number}`;
        } else if (typeof phoneData === "string") {
          phoneValue = phoneData;
        }
      }

      setWelcomeFormData({
        firstname: profile.firstname || "",
        lastname: profile.lastname || "",
        phonenumber: phoneValue,
      });
    } else {
      setShowWelcomeModal(false);
    }
  }, [profile]);

  // Handle welcome form input changes
  const handleWelcomeFormChange = (e) => {
    const { name, value } = e.target;
    setWelcomeFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle phone input change
  const handleWelcomePhoneChange = (value) => {
    setWelcomeFormData((prev) => ({
      ...prev,
      phonenumber: value,
    }));
  };

  // Helper function to parse phone number using libphonenumber-js
  const parsePhoneNumber = (phoneString) => {
    try {
      // Phone string from react-phone-input-2 comes as "971501234567"
      // We need to add + prefix for libphonenumber-js
      const phoneWithPlus = phoneString.startsWith("+")
        ? phoneString
        : `+${phoneString}`;
      const phoneNumber = parsePhoneNumberFromString(phoneWithPlus);

      if (phoneNumber && phoneNumber.isValid()) {
        return {
          countryCode: phoneNumber.countryCallingCode,
          number: phoneNumber.nationalNumber,
        };
      } else {
        // Fallback: assume first 1-3 digits are country code
        for (let len = 3; len >= 1; len--) {
          const possibleCode = phoneString.substring(0, len);
          if (/^\d+$/.test(possibleCode) && phoneString.length > len) {
            return {
              countryCode: possibleCode,
              number: phoneString.substring(len),
            };
          }
        }
        return { countryCode: "", number: phoneString };
      }
    } catch (error) {
      console.warn("Phone parsing error:", error);
      // Fallback: assume first 2 digits are country code
      return {
        countryCode: phoneString.substring(0, 2),
        number: phoneString.substring(2),
      };
    }
  };

  // Submit welcome form
  const handleWelcomeFormSubmit = async () => {
    // Validate fields
    if (!welcomeFormData.firstname.trim()) {
      antMessage.error("Please enter your first name");
      return;
    }
    // if (!welcomeFormData.lastname.trim()) {
    //   antMessage.error("Please enter your last name");
    //   return;
    // }
    if (
      !welcomeFormData.phonenumber ||
      welcomeFormData.phonenumber.length < 10
    ) {
      antMessage.error("Please enter a valid phone number");
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert phone number to required format
      const formData = new FormData();
      formData.append("firstname", welcomeFormData.firstname);
      formData.append("lastname", welcomeFormData.lastname);

      // Parse the phone number to extract country code and number
      const phoneObj = parsePhoneNumber(welcomeFormData.phonenumber);
      formData.append("phonenumbers", JSON.stringify([phoneObj]));

      const response = await api.put("/editProfile", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === "success") {
        antMessage.success("Profile completed successfully!");
        // Refresh profile data
        await dispatch(fetchProfile());
        setShowWelcomeModal(false);
      } else {
        antMessage.error(response.data.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      antMessage.error(
        error.response?.data?.message ||
          "Error updating profile. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center", paddingTop: "10px" }}>
          <h3 style={{ margin: 0, color: "#1890ff" }}>
            👋 Welcome to VoycellCallCenter!
          </h3>
        </div>
      }
      open={showWelcomeModal}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={500}
    >
      <div style={{ padding: "20px 0" }}>
        <Text
          style={{
            display: "block",
            marginBottom: "20px",
            textAlign: "center",
          }}
        >
          Let's complete your profile to get started
        </Text>

        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              First Name <span style={{ color: "red" }}>*</span>
            </label>
            <Input
              size="large"
              name="firstname"
              value={welcomeFormData.firstname}
              onChange={handleWelcomeFormChange}
              placeholder="Enter your first name"
              maxLength={50}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              Last Name
            </label>
            <Input
              size="large"
              name="lastname"
              value={welcomeFormData.lastname}
              onChange={handleWelcomeFormChange}
              placeholder="Enter your last name"
              maxLength={50}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              Phone Number <span style={{ color: "red" }}>*</span>
            </label>
            <PhoneInput
              country={"ae"}
              value={welcomeFormData.phonenumber}
              onChange={handleWelcomePhoneChange}
              enableSearch
              inputProps={{
                name: "phone",
                required: true,
              }}
              inputStyle={{
                // height: "40px",
                // border: "1px solid #d9d9d9",
                // borderRadius: "4px",
                width: "100%",
                // paddingLeft: "48px",
                // fontSize: "14px",
              }}
              // buttonStyle={{
              //   border: "1px solid #d9d9d9",
              //   borderRight: "none",
              //   height: "40px",
              //   backgroundColor: "transparent",
              // }}
              containerStyle={{ width: "100%" }}
            />
          </div>

          <Button
            type="primary"
            size="large"
            block
            onClick={handleWelcomeFormSubmit}
            loading={isSubmitting}
            style={{ marginTop: "10px" }}
          >
            Continue
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

export default ProfileCompletionModal;
